import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { X } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";

export const INFO_PIN_COLOR = "#3B9DF2";

export const PIN_TYPES = [
  { key: "info", label: "Info", color: "#3B9DF2", glyph: null },
  { key: "flame", label: "Fire", color: "#F2542D", glyph: "🔥" },
  { key: "evac", label: "Evac", color: "#E63946", glyph: "🏃" },
  { key: "caution", label: "Caution", color: "#FFB703", glyph: "⚠️" },
  { key: "closed", label: "Road Closed", color: "#9B2226", glyph: "⛔" },
  { key: "debris", label: "Debris/Mudflow", color: "#8B5E3C", glyph: "🪨" },
  { key: "shelter", label: "Shelter", color: "#2A9D8F", glyph: "🏠" },
];

export const pinType = (key) => PIN_TYPES.find((t) => t.key === key) || PIN_TYPES[0];

const _pinIcons = {};
export function pinIcon(key) {
  const t = pinType(key);
  if (!_pinIcons[t.key]) {
    const inner = t.glyph
      ? `<circle cx="12" cy="8.6" r="4.4" fill="#FFFFFF"/>
        <text x="12" y="11.1" font-size="6.4" text-anchor="middle">${t.glyph}</text>`
      : `<circle cx="12" cy="8.6" r="3.4" fill="#FFFFFF"/>
        <rect x="11.2" y="7.6" width="1.6" height="3.4" rx="0.8" fill="${t.color}"/>
        <circle cx="12" cy="6.3" r="0.9" fill="${t.color}"/>`;
    _pinIcons[t.key] = L.divIcon({
      className: "info-pin-marker",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
        <path d="M12 1.8c-4 0-7.2 3.1-7.2 7 0 5.2 7.2 13.4 7.2 13.4s7.2-8.2 7.2-13.4c0-3.9-3.2-7-7.2-7z" fill="${t.color}" stroke="#0A0D0C" stroke-width="1.4" stroke-linejoin="round"/>
        ${inner}
      </svg>`,
      iconSize: [30, 30],
      iconAnchor: [15, 28],
      popupAnchor: [0, -26],
    });
  }
  return _pinIcons[t.key];
}

const fmtDT = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function videoEmbed(url) {
  const yt = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1` };
  if (/facebook\.com|fb\.watch/.test(url || "")) {
    return { kind: "iframe", src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true` };
  }
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "")) return { kind: "video", src: url };
  return { kind: "iframe", src: url };
}

function videoThumb(p) {
  if (p.video_id) return { kind: "video", src: `${fileUrl(p.video_id)}#t=0.1` };
  const yt = (p.video_url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return { kind: "img", src: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(p.video_url || "")) return { kind: "video", src: `${p.video_url}#t=0.1` };
  return { kind: "none" };
}

export default function InfoPinsLayer({ pins = [], canManage = false, draggable = false, onEdit, onDelete, onMove }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      {pins.map((p) => (
        <Marker
          key={`pin-${p.id}-${p.icon || "info"}`}
          position={[p.latitude, p.longitude]}
          icon={pinIcon(p.icon)}
          zIndexOffset={1100}
          draggable={draggable}
          eventHandlers={
            draggable
              ? {
                  dragend: (e) => {
                    const ll = e.target.getLatLng();
                    onMove?.(p, ll.lat, ll.lng);
                  },
                }
              : undefined
          }
        >
          <Popup>
            <div className="min-w-[210px]" data-testid={`info-pin-popup-${p.id}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: pinType(p.icon).color }}>
                {pinType(p.icon).key === "info" ? "Community Info" : `Community · ${pinType(p.icon).label}`}
              </div>
              <div className="font-display font-bold text-sm text-tac-text mb-1 leading-snug">
                {p.title}
              </div>
              {(p.image_ids || []).length > 0 && (
                <div className="flex gap-1 mb-2">
                  {p.image_ids.map((fid) => (
                    <div
                      key={fid}
                      className="relative overflow-hidden rounded-sm border border-tac-border"
                      style={{ width: p.image_ids.length > 1 ? "calc(50% - 2px)" : "100%", paddingTop: p.image_ids.length > 1 ? "calc(50% - 2px)" : "100%" }}
                    >
                      <img
                        src={fileUrl(fid)}
                        alt="Pin photo"
                        data-testid={`info-pin-photo-${fid}`}
                        onClick={() => setLightbox({ kind: "image", src: fileUrl(fid) })}
                        className="absolute inset-0 w-full h-full cursor-pointer object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
              {(p.video_id || p.video_url) && (() => {
                const thumb = videoThumb(p);
                return (
                  <button
                    type="button"
                    data-testid={`info-pin-video-btn-${p.id}`}
                    onClick={() => {
                      if (p.video_id) {
                        setLightbox({ kind: "video", src: fileUrl(p.video_id), title: p.title });
                        return;
                      }
                      const emb = videoEmbed(p.video_url);
                      setLightbox({ ...emb, title: p.title, orig: p.video_url });
                    }}
                    className="relative block w-full mb-2 rounded-sm overflow-hidden border"
                    style={{ borderColor: INFO_PIN_COLOR, paddingTop: "56.25%" }}
                  >
                    {thumb.kind === "img" && (
                      <img src={thumb.src} alt="Video preview" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    {thumb.kind === "video" && (
                      <video src={thumb.src} preload="metadata" muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    {thumb.kind === "none" && <span className="absolute inset-0 bg-black/70" />}
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[15px] pl-0.5"
                        style={{ background: "rgba(0,0,0,0.55)", border: `2px solid ${INFO_PIN_COLOR}` }}
                      >
                        ▶
                      </span>
                    </span>
                    <span
                      className="absolute bottom-1 left-0 right-0 text-center text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}
                    >
                      Watch video
                    </span>
                  </button>
                );
              })()}
              {p.note && (
                <div className="text-[12px] text-tac-dim leading-snug mb-2 whitespace-pre-line">
                  {p.note}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {p.link_url && (
                  <a
                    href={p.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`info-pin-link-${p.id}`}
                    className="inline-flex text-[11px] font-bold uppercase tracking-wider hover:underline"
                    style={{ color: INFO_PIN_COLOR }}
                  >
                    {/facebook\.com|fb\.com/i.test(p.link_url) ? "View on Facebook →" : "View post →"}
                  </a>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`info-pin-directions-${p.id}`}
                  className="inline-flex text-[11px] font-bold uppercase tracking-wider hover:underline"
                  style={{ color: INFO_PIN_COLOR }}
                >
                  Directions ↗
                </a>
              </div>
              <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
                {p.created_by || "staff"} · {fmtDT(p.created_at)}
                {p.expires_at && ` · expires ${fmtDT(p.expires_at)}`}
              </div>
              {canManage && (
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    data-testid={`info-pin-edit-${p.id}`}
                    onClick={() => onEdit?.(p)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-tac-border rounded-sm text-tac-text hover:border-tac-borderBright"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`info-pin-delete-${p.id}`}
                    onClick={() => window.confirm("Delete this pin?") && onDelete?.(p)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-tac-danger/60 rounded-sm text-tac-danger hover:bg-tac-danger/10"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      ))}

      {lightbox &&
        createPortal(
          <div
            data-testid="info-pin-lightbox"
            className="fixed inset-0 z-[1300] bg-black/85 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="info-pin-lightbox-close"
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
              onClick={() => setLightbox(null)}
            >
              <X size={24} weight="bold" />
            </button>
            {lightbox.kind === "image" ? (
              <img
                src={lightbox.src}
                alt="Pin full size"
                className="max-h-[90vh] max-w-[95vw] object-contain rounded-sm"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div
                className="w-full max-w-4xl bg-tac-bg border border-tac-border rounded-sm shadow-2xl flex flex-col overflow-hidden"
                data-testid="info-pin-video-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-tac-border">
                  <span className="text-tac-primary font-bold">▶</span>
                  <div className="text-sm font-display font-bold text-tac-text truncate">
                    {lightbox.title || "Pin video"}
                  </div>
                  {lightbox.orig && (
                    <a
                      href={lightbox.orig}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="info-pin-video-original-link"
                      className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text border border-tac-border rounded-sm transition-colors whitespace-nowrap"
                    >
                      Open original ↗
                    </a>
                  )}
                  <button
                    data-testid="info-pin-video-close"
                    onClick={() => setLightbox(null)}
                    className={`${lightbox.orig ? "" : "ml-auto "}p-1.5 text-tac-dim hover:text-tac-text border border-tac-border rounded-sm transition-colors`}
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
                <div className="bg-black flex items-center justify-center">
                  {lightbox.kind === "video" ? (
                    <video
                      src={lightbox.src}
                      controls
                      autoPlay
                      playsInline
                      data-testid="info-pin-video-player"
                      className="w-full max-h-[70vh]"
                    />
                  ) : (
                    <iframe
                      src={lightbox.src}
                      title="Pin video"
                      data-testid="info-pin-video-iframe"
                      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                      allowFullScreen
                      className={lightbox.portrait ? "border-0 mx-auto" : "w-full border-0"}
                      style={lightbox.portrait ? { height: "min(70vh, 560px)", aspectRatio: "9 / 16" } : { height: "min(70vh, 540px)" }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

